import {
  SurveyQuestionType,
  type APISurveyAttributes,
  type ArdoqId,
  type PersistedReferenceQuestion,
  type PersistedSurveyQuestion,
} from '@ardoq/api-types';
import type { TourStep } from '../types';

/**
 * The two questions the tour's form steps spotlight: the one that offers
 * Generate description, and the one that points at another component.
 */
export type AnswerSurveysQuestions = {
  fieldsQuestionId: ArdoqId | null;
  ownerQuestionId: ArdoqId | null;
};

const isDescriptionQuestion = (question: PersistedSurveyQuestion): boolean =>
  question.type === SurveyQuestionType.ATTRIBUTE &&
  question.properties.attributeName === 'description';

const isReferenceQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedReferenceQuestion =>
  question.type === SurveyQuestionType.REFERENCE;

const MENTIONS_OWNER = /\bown/i;

const isOwnerReferenceQuestion = ({
  label,
  properties: { referenceTypeName },
}: PersistedReferenceQuestion): boolean =>
  MENTIONS_OWNER.test(referenceTypeName ?? '') || MENTIONS_OWNER.test(label);

const toOwnerQuestionId = (
  questions: PersistedSurveyQuestion[]
): ArdoqId | null => {
  const references = questions.filter(isReferenceQuestion);
  return (
    (references.find(isOwnerReferenceQuestion) ?? references[0])?._id ?? null
  );
};

export const toAnswerSurveysQuestions = (
  questions: PersistedSurveyQuestion[]
): AnswerSurveysQuestions => ({
  fieldsQuestionId: questions.find(isDescriptionQuestion)?._id ?? null,
  ownerQuestionId: toOwnerQuestionId(questions),
});

type SurveyQuestions = Pick<APISurveyAttributes, 'questions'>;

export const hasAnswerSurveysOwnerQuestion = ({
  questions,
}: SurveyQuestions): boolean =>
  questions.filter(isReferenceQuestion).some(isOwnerReferenceQuestion);

export const isSuitableAnswerSurveysSurvey = ({
  questions,
}: SurveyQuestions): boolean => {
  const { fieldsQuestionId, ownerQuestionId } =
    toAnswerSurveysQuestions(questions);
  return Boolean(fieldsQuestionId && ownerQuestionId);
};

export const toAnswerSurveysSteps = (
  steps: TourStep[],
  survey: SurveyQuestions | null
): TourStep[] => {
  if (!survey) return steps;
  const { fieldsQuestionId, ownerQuestionId } = toAnswerSurveysQuestions(
    survey.questions
  );
  return steps.filter(({ id }) => {
    if (id === 'fill-fields') return fieldsQuestionId !== null;
    if (id === 'select-owner') return ownerQuestionId !== null;
    return true;
  });
};
